import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  ChartBarIcon,
  ServerIcon,
  DocumentTextIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CpuChipIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import { projectsApi, monitoringApi } from '@/lib/api';
import { formatDistanceToNow } from 'date-fns';

interface HealthStatus {
  status: 'healthy' | 'unhealthy';
  timestamp: string;
  uptime: number;
  memory: {
    heapUsed: number;
    heapTotal: number;
    external: number;
    rss: number;
  };
  database: 'connected' | 'disconnected';
  version: string;
}

interface SystemStatus {
  timestamp: string;
  uptime: number;
  memory: {
    process: {
      heapUsed: number;
      heapTotal: number;
      external: number;
      rss: number;
    };
    system: {
      total: number;
      free: number;
      used: number;
    };
  };
  cpu: {
    count: number;
    loadAverage: number[];
  };
  platform: {
    arch: string;
    platform: string;
    version: string;
    hostname: string;
  };
  node: {
    version: string;
    pid: number;
  };
}

export default function MonitoringDashboard() {
  const { id: projectId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [metrics, setMetrics] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState<'overview' | 'metrics' | 'logs'>('overview');

  // Fetch project details
  const { data: projectResponse } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Fetch health status
  const { data: healthResponse } = useQuery(
    'health-status',
    () => monitoringApi.getHealth(),
    {
      enabled: isAuthenticated,
      refetchInterval: 5000, // Refetch every 5 seconds
    }
  );

  // Fetch system status
  const { data: systemResponse } = useQuery(
    'system-status',
    () => monitoringApi.getSystemStatus(),
    {
      enabled: isAuthenticated,
      refetchInterval: 10000, // Refetch every 10 seconds
    }
  );

  // Fetch Prometheus metrics
  const { data: metricsResponse } = useQuery(
    'prometheus-metrics',
    () => monitoringApi.getMetrics(),
    {
      enabled: isAuthenticated && selectedTab === 'metrics',
      refetchInterval: 15000, // Refetch every 15 seconds
    }
  );

  useEffect(() => {
    if (metricsResponse) {
      setMetrics(metricsResponse);
    }
  }, [metricsResponse]);

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const health: HealthStatus = healthResponse || {};
  const system: SystemStatus = systemResponse || {};

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'unhealthy': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'metrics', name: 'Metrics', icon: ServerIcon },
    { id: 'logs', name: 'Logs', icon: DocumentTextIcon },
  ];

  const project = projectResponse?.data?.project;

  return (
      <div className="px-4 sm:px-0">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-gray-900">
              System Monitoring
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Monitor system health, performance metrics, and application logs for {project?.name || 'this project'}.
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                  selectedTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <div className="mt-6 space-y-6">
            {/* Health Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-md ${getHealthStatusColor(health.status)}`}>
                      {health.status === 'healthy' ? (
                        <CheckCircleIcon className="h-6 w-6" />
                      ) : (
                        <ExclamationTriangleIcon className="h-6 w-6" />
                      )}
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        System Health
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {health.status?.charAt(0).toUpperCase() + health.status?.slice(1) || 'Unknown'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-md ${
                      health.database === 'connected' 
                        ? 'text-green-600 bg-green-100' 
                        : 'text-red-600 bg-red-100'
                    }`}>
                      <DocumentTextIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Database
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {health.database?.charAt(0).toUpperCase() + health.database?.slice(1) || 'Unknown'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="p-3 rounded-md text-blue-600 bg-blue-100">
                      <ClockIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Uptime
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {health.uptime ? formatUptime(health.uptime) : 'Unknown'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="p-3 rounded-md text-purple-600 bg-purple-100">
                      <CpuChipIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Version
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {health.version || 'Unknown'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* System Information */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Memory Usage</h2>
                {health.memory ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Heap Used</span>
                      <span className="text-sm font-medium">{formatBytes(health.memory.heapUsed)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Heap Total</span>
                      <span className="text-sm font-medium">{formatBytes(health.memory.heapTotal)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">RSS</span>
                      <span className="text-sm font-medium">{formatBytes(health.memory.rss)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">External</span>
                      <span className="text-sm font-medium">{formatBytes(health.memory.external)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No memory data available</p>
                )}
              </div>

              <div className="card">
                <h2 className="text-lg font-medium text-gray-900 mb-4">System Information</h2>
                {system.platform ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Platform</span>
                      <span className="text-sm font-medium">{system.platform.platform}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Architecture</span>
                      <span className="text-sm font-medium">{system.platform.arch}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Node.js</span>
                      <span className="text-sm font-medium">{system.node.version}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">CPU Cores</span>
                      <span className="text-sm font-medium">{system.cpu.count}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Hostname</span>
                      <span className="text-sm font-medium">{system.platform.hostname}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No system data available</p>
                )}
              </div>
            </div>

            {/* Additional System Metrics */}
            {system.memory && (
              <div className="card">
                <h2 className="text-lg font-medium text-gray-900 mb-4">System Memory</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatBytes(system.memory.system.total)}
                    </div>
                    <div className="text-sm text-gray-500">Total Memory</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-semibold text-red-600">
                      {formatBytes(system.memory.system.used)}
                    </div>
                    <div className="text-sm text-gray-500">Used Memory</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-semibold text-green-600">
                      {formatBytes(system.memory.system.free)}
                    </div>
                    <div className="text-sm text-gray-500">Free Memory</div>
                  </div>
                </div>
                
                {/* Memory Usage Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-500 mb-1">
                    <span>Memory Usage</span>
                    <span>{Math.round((system.memory.system.used / system.memory.system.total) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${(system.memory.system.used / system.memory.system.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Metrics Tab */}
        {selectedTab === 'metrics' && (
          <div className="mt-6">
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Prometheus Metrics</h2>
                <span className="text-sm text-gray-500">
                  Last updated: {formatDistanceToNow(new Date(), { addSuffix: true })}
                </span>
              </div>
              
              {metrics ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  <pre className="text-xs text-gray-700 overflow-auto max-h-96 whitespace-pre-wrap">
                    {metrics}
                  </pre>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CircleStackIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No metrics available</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Metrics are being collected. Please wait a moment.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {selectedTab === 'logs' && (
          <div className="mt-6">
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Application Logs</h2>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-4">
                  Logs are written to files in the <code className="bg-gray-200 px-1 rounded">logs/</code> directory with daily rotation:
                </p>
                
                <div className="space-y-2 text-sm">
                  <div>
                    <code className="bg-gray-200 px-2 py-1 rounded font-mono">combined-YYYY-MM-DD.log</code>
                    <span className="ml-2 text-gray-600">- All log levels</span>
                  </div>
                  <div>
                    <code className="bg-gray-200 px-2 py-1 rounded font-mono">error-YYYY-MM-DD.log</code>
                    <span className="ml-2 text-gray-600">- Error-level logs only</span>
                  </div>
                  <div>
                    <code className="bg-gray-200 px-2 py-1 rounded font-mono">access-YYYY-MM-DD.log</code>
                    <span className="ml-2 text-gray-600">- HTTP access logs</span>
                  </div>
                  <div>
                    <code className="bg-gray-200 px-2 py-1 rounded font-mono">exceptions.log</code>
                    <span className="ml-2 text-gray-600">- Unhandled exceptions</span>
                  </div>
                  <div>
                    <code className="bg-gray-200 px-2 py-1 rounded font-mono">rejections.log</code>
                    <span className="ml-2 text-gray-600">- Unhandled promise rejections</span>
                  </div>
                </div>

                <div className="mt-4 text-sm text-gray-600">
                  <p>Log format is structured JSON with:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Request ID for tracing</li>
                    <li>User context when available</li>
                    <li>Performance metrics</li>
                    <li>Error stack traces</li>
                    <li>Database operation timing</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}